import React, { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";
import api from "@/lib/api";
import { getSocket } from "@/lib/socket";

export default function OrderChatModal({ order, onClose, viewer = "admin" }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const prefilled = useRef(false);
  const onum = order.order_number;

  const load = async () => {
    try { const { data } = await api.get(`/chat/messages?order_number=${encodeURIComponent(onum)}`); setMsgs(data); } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [onum]);
  // Friendly opener so staff instantly see which order a customer's first message is about.
  useEffect(() => {
    if (viewer === "customer" && !prefilled.current && msgs.length === 0 && !text) {
      prefilled.current = true;
      setText(`About order ${onum} — `);
    }
    /* eslint-disable-next-line */
  }, [msgs, viewer, onum]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => {
    const s = getSocket();
    const onMsg = (m) => { if (m.order_number === onum) load(); };
    s.on("chat_message", onMsg);
    return () => s.off("chat_message", onMsg);
    // eslint-disable-next-line
  }, [onum]);

  const send = async () => {
    const t = text.trim(); if (!t) return; setText("");
    try { await api.post(`/chat/messages?order_number=${encodeURIComponent(onum)}`, { text: t }); load(); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="order-chat-modal">
      <div className="bg-white w-full sm:max-w-md h-[80vh] sm:h-[560px] sm:rounded-2xl flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-metros-black text-white px-4 py-3 flex items-center justify-between">
          <div><p className="font-heading font-bold">Chat · {onum}</p><p className="text-xs text-white/70">{viewer === "customer" ? "About this order" : (order.customer?.name || "Customer")}</p></div>
          <button onClick={onClose} data-testid="order-chat-close" className="text-white/80 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
          {msgs.length === 0 && <p className="text-center text-xs text-gray-400 mt-6">No messages yet. Send us a message about this order.</p>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === viewer ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === viewer ? "bg-metros-red text-white rounded-br-sm" : "bg-white border border-gray-200 rounded-bl-sm"}`}>{m.text}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-2 border-t border-gray-100 flex items-center gap-2">
          <input data-testid="order-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-metros-red" />
          <button data-testid="order-chat-send" onClick={send} className="w-10 h-10 rounded-full bg-metros-red text-white flex items-center justify-center shrink-0"><Send size={16} /></button>
        </div>
      </div>
    </div>
  );
}
